import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { formatNumber } from '@/utils/format';
import { ApiError } from '@/api/client/abpError';
import { suspiciousTradesApi } from '@/api/services/suspiciousTrades';
import { MiFIDClassification, type TsAlertInfoDto } from '@/api/types/traderSurveillance';

const MIFID_LABEL: Record<MiFIDClassification, string> = {
  [MiFIDClassification.Retail]: 'Retail',
  [MiFIDClassification.PerSeProfessional]: 'Per Se Professional',
  [MiFIDClassification.ProfessionalOnRequest]: 'Professional On Request',
  [MiFIDClassification.EligibleCounterparty]: 'Eligible Counterparty',
  [MiFIDClassification.Default]: 'Default',
};

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="d-flex justify-content-between border-bottom py-2">
      <span className="fw-semibold">{label}</span>
      <span>{value}</span>
    </div>
  );
}

const num = (v?: number | null) => formatNumber(v ?? 0, 2);

interface AlertInfoPanelProps {
  alertId: number;
  canOperate: boolean;
  onClose: () => void;
  // Reload grid + counts + badge after a resolve/reopen/post-comment.
  onChanged: () => void;
  // Open the Trade/Deal grid for the current alert.
  onShowTrade: () => void;
  // Open the Audit Trail grid for the current alert.
  onShowAudit: () => void;
}

export function AlertInfoPanel({ alertId, canOperate, onClose, onChanged, onShowTrade, onShowAudit }: AlertInfoPanelProps) {
  const toast = useToast();
  const info = useQuery({
    queryKey: ['ts-alert-info', alertId],
    queryFn: () => suspiciousTradesApi.getAlertInfo(alertId),
  });
  const [comment, setComment] = useState('');

  const d = info.data;
  useEffect(() => {
    setComment(d?.tsComment ?? '');
  }, [d]);

  async function op(method: 'resolveAlert' | 'reopenAlert' | 'postComment') {
    try {
      const msg = await suspiciousTradesApi[method]({ alertId, tsComment: comment });
      if (!msg) {
        toast.warning('Please add a comment.');
        return;
      }
      toast.success(msg);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'The operation failed.');
    }
  }

  const mifidLabel = (info: TsAlertInfoDto) =>
    info.mifidClassificationName ?? (info.mifidClassification != null ? MIFID_LABEL[info.mifidClassification] : null);

  return (
    <div className="custom-card card h-100">
      <div className="card-header d-flex justify-content-between align-items-center pb-3 pt-2 border-0">
        <h6 className="mb-0">{`Alert Info - ${alertId}`}</h6>
        <button type="button" className="btn btn-sm" title="Close" onClick={onClose}>
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="card-body">
        {info.isLoading ? (
          <div className="text-center text-muted py-3">
            <i className="fa fa-spinner fa-spin me-1" />
            Loading…
          </div>
        ) : info.isError || !d ? (
          <div className="text-center text-danger py-3">Could not load the alert.</div>
        ) : (
          <>
            {d.profileTag && <InfoRow label="Profile Tag" value={d.profileTag} />}
            {d.susTypesDisplay && <InfoRow label="Triggered By" value={d.susTypesDisplay} />}
            {d.byUniqueId ? (
              <InfoRow label="Unique Id" value={d.uniqueId} />
            ) : (
              <>
                <InfoRow label="Login" value={d.login} />
                <InfoRow label="Connector" value={d.connector} />
                <InfoRow label="Unique Id" value={d.uniqueId} />
              </>
            )}
            {d.group && <InfoRow label="Group" value={d.group} />}
            {d.country && <InfoRow label="Country" value={d.country} />}
            {mifidLabel(d) && <InfoRow label="MiFID Classification" value={mifidLabel(d)} />}

            {d.ticket ? <InfoRow label={d.dealAsTradeType ? 'Deal' : 'Ticket'} value={d.ticket} /> : null}

            {d.userAsAlertType && !d.isCumulativeProfit && (
              <>
                <InfoRow label="Equity AC" value={num(d.value)} />
                <InfoRow label="Equity RC" value={num(d.valueRc)} />
                <InfoRow label="Margin Level" value={num(d.marginLevel)} />
              </>
            )}

            {d.profitRc != null && (
              <InfoRow label={d.isCumulativeProfit ? 'Cumulative Profit RC' : 'Profit RC'} value={num(d.profitRc)} />
            )}

            <div className="mt-2">
              <label className="form-label">Comment</label>
              <textarea className="form-control" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>

            <div className="mt-2 text-end d-flex flex-wrap justify-content-end gap-1">
              {canOperate && (
                <>
                  <AsyncButton className="btn btn-outline-secondary btn-sm" onClick={() => op('postComment')}>
                    Post Comment
                  </AsyncButton>
                  {d.resolved === 1 ? (
                    <AsyncButton className="btn btn-outline-warning btn-sm" onClick={() => op('reopenAlert')}>
                      Reopen
                    </AsyncButton>
                  ) : (
                    <AsyncButton className="btn btn-outline-success btn-sm" onClick={() => op('resolveAlert')}>
                      Resolve
                    </AsyncButton>
                  )}
                </>
              )}
              {d.dealAsTradeType ? (
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={onShowTrade}>
                  Show Deal
                </button>
              ) : d.tradeAsTradeType ? (
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={onShowTrade}>
                  Show Trade
                </button>
              ) : null}
              <button type="button" className="btn btn-outline-info btn-sm" onClick={onShowAudit}>
                Audit Trail
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
